import React, { useState } from 'react';
import { guestsApi } from '../../_lib/api-client';
import { CreateGuestInput } from '../../_graphql/generated/types';

interface BulkGuestImportProps {
  eventId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ParsedGuest {
  name: string;
  email?: string;
  phone?: string;
}

const BulkGuestImport: React.FC<BulkGuestImportProps> = ({ eventId, onSuccess, onCancel }) => {
  const [inputText, setInputText] = useState('');
  const [parsedGuests, setParsedGuests] = useState<ParsedGuest[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importError, setImportError] = useState<string | null>(null);

  const parseGuests = () => {
    setParseError(null);
    const lines = inputText.trim().split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      setParseError('Please enter at least one guest');
      return;
    }

    const guests: ParsedGuest[] = [];
    const errors: string[] = [];

    lines.forEach((line, index) => {
      const parts = line.split(',').map(p => p.trim());
      
      if (parts.length === 0 || !parts[0]) {
        errors.push(`Line ${index + 1}: Name is required`);
        return;
      }

      const guest: ParsedGuest = { name: parts[0] };
      
      // Try to detect email and phone in remaining parts
      for (let i = 1; i < parts.length; i++) {
        const part = parts[i];
        if (part.includes('@')) {
          guest.email = part;
        } else if (part.match(/^[\d\s\-\+\(\)]+$/)) {
          guest.phone = part;
        }
      }

      guests.push(guest);
    });

    if (errors.length > 0) {
      setParseError(errors.join('\n'));
      return;
    }

    setParsedGuests(guests);
  };

  const handleImport = async () => {
    if (parsedGuests.length === 0) return;

    setIsImporting(true);
    setImportError(null);
    setImportProgress({ current: 0, total: parsedGuests.length });

    try {
      // Import guests one by one to track progress
      for (let i = 0; i < parsedGuests.length; i++) {
        const guest = parsedGuests[i];
        const input: CreateGuestInput = {
          eventId,
          name: guest.name,
          email: guest.email,
          phone: guest.phone
        };

        await guestsApi.create(input);
        setImportProgress({ current: i + 1, total: parsedGuests.length });
      }

      onSuccess();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Failed to import guests');
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {!parsedGuests.length ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter guest information (one per line)
            </label>
            <div className="text-xs text-gray-500 mb-2">
              Format: Name, Email, Phone (email and phone are optional)
              <br />
              Example: John Doe, john@example.com, 555-1234
            </div>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="w-full h-48 px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-rose-400 bg-rose-50/30"
              placeholder="Jane Smith, jane@email.com, 555-5678
Bob Johnson
Alice Williams, alice@email.com"
            />
          </div>

          {parseError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 whitespace-pre-line">{parseError}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={parseGuests}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-full hover:shadow-md transition-all font-medium"
            >
              Parse Guests
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-medium"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div>
            <h4 className="font-medium text-gray-700 mb-2">
              Preview ({parsedGuests.length} guests to import)
            </h4>
            <div className="max-h-64 overflow-y-auto border border-rose-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-rose-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-100">
                  {parsedGuests.map((guest, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2">{guest.name}</td>
                      <td className="px-3 py-2 text-gray-500">{guest.email || '-'}</td>
                      <td className="px-3 py-2 text-gray-500">{guest.phone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {isImporting && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-600">
                Importing... {importProgress.current} / {importProgress.total}
              </p>
              <div className="mt-2 w-full bg-blue-100 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {importError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{importError}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleImport}
              disabled={isImporting}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-400 to-teal-500 text-white rounded-full hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
            >
              {isImporting ? 'Importing...' : 'Import Guests'}
            </button>
            <button
              type="button"
              onClick={() => setParsedGuests([])}
              disabled={isImporting}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
            >
              Back to Edit
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkGuestImport;